import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';

// What the backend actually returns (snake_case)
interface BackendAuditLogEntry {
  id: string;
  team_id: string;
  user_id?: string;
  action: string;
  resource_type: string;
  resource_id: string;
  metadata?: {
    project_id?: string;
    project_name?: string;
    environment_id?: string;
    environment_name?: string;
    secret_key?: string;
    version?: number;
    bulk_count?: number;
    // Token-related metadata
    token_name?: string;
    // Team member-related metadata
    member_email?: string;
    member_role?: string;
  };
  ip_address?: string;
  created_at: string;
}

// What the frontend uses (camelCase)
export interface AuditLogEntry {
  id: string;
  userId?: string;
  action: string;
  resourceType: string;
  resourceId: string;
  resourceName?: string;
  projectId?: string;
  projectName?: string;
  environmentId?: string;
  environmentName?: string;
  metadata?: Record<string, unknown>;
  ipAddress?: string;
  createdAt: string;
}

// Backend uses past tense: secret.created, secret.updated, etc.
export type AuditAction =
  // Secret actions
  | 'secret.created'
  | 'secret.read'
  | 'secret.updated'
  | 'secret.deleted'
  // Project actions
  | 'project.created'
  | 'project.updated'
  | 'project.deleted'
  // Environment actions
  | 'environment.created'
  | 'environment.deleted'
  // Token actions
  | 'token.created'
  | 'token.revoked'
  // Team member actions
  | 'team.member_invited'
  | 'team.member_removed';

export const AUDIT_ACTIONS: { value: AuditAction | ''; label: string }[] = [
  { value: '', label: 'All actions' },
  // Secret actions
  { value: 'secret.created', label: 'Secret created' },
  { value: 'secret.read', label: 'Secret read' },
  { value: 'secret.updated', label: 'Secret updated' },
  { value: 'secret.deleted', label: 'Secret deleted' },
  // Project actions
  { value: 'project.created', label: 'Project created' },
  { value: 'project.updated', label: 'Project updated' },
  { value: 'project.deleted', label: 'Project deleted' },
  // Environment actions
  { value: 'environment.created', label: 'Environment created' },
  { value: 'environment.deleted', label: 'Environment deleted' },
  // Token actions
  { value: 'token.created', label: 'Token created' },
  { value: 'token.revoked', label: 'Token revoked' },
  // Team member actions
  { value: 'team.member_invited', label: 'Member invited' },
  { value: 'team.member_removed', label: 'Member removed' },
];

// Backend response format
interface BackendAuditLogsResponse {
  logs: BackendAuditLogEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface AuditLogsFilters {
  teamId?: string;
  projectId?: string;
  userId?: string;
  action?: AuditAction | '';
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Get the resource name from metadata based on resource type
 */
function getResourceName(resourceType: string, metadata?: BackendAuditLogEntry['metadata']): string | undefined {
  if (!metadata) return undefined;

  switch (resourceType) {
    case 'secret':
      // For bulk operations, show count instead of individual secret name
      if (metadata.bulk_count && metadata.bulk_count > 0) {
        return `${metadata.bulk_count} secrets`;
      }
      return metadata.secret_key;
    case 'project':
      return metadata.project_name;
    case 'environment':
      return metadata.environment_name;
    case 'token':
      return metadata.token_name;
    case 'team':
      return metadata.member_email;
    default:
      return undefined;
  }
}

/**
 * Transform backend audit log entry to frontend format
 */
function transformAuditLog(entry: BackendAuditLogEntry): AuditLogEntry {
  return {
    id: entry.id,
    userId: entry.user_id,
    action: entry.action,
    resourceType: entry.resource_type,
    resourceId: entry.resource_id,
    resourceName: getResourceName(entry.resource_type, entry.metadata),
    projectId: entry.metadata?.project_id,
    projectName: entry.metadata?.project_name,
    environmentId: entry.metadata?.environment_id,
    environmentName: entry.metadata?.environment_name,
    metadata: entry.metadata,
    ipAddress: entry.ip_address,
    createdAt: entry.created_at,
  };
}

/**
 * Hook for fetching audit logs with filters
 */
export function useAuditLogs(filters: AuditLogsFilters = {}) {
  const {
    teamId,
    projectId,
    userId,
    action,
    startDate,
    endDate,
    page = 1,
    pageSize = 25,
  } = filters;

  const queryKey = ['auditLogs', { teamId, projectId, userId, action, startDate, endDate, page, pageSize }];

  const auditLogsQuery = useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams();

      // Required parameter
      if (teamId) params.append('team_id', teamId);

      // Note: Backend doesn't support project_id or user_id filtering currently
      // These are kept for future compatibility
      if (action) params.append('action', action);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      // Backend uses limit/offset, not page/page_size
      const limit = pageSize;
      const offset = (page - 1) * pageSize;
      params.append('limit', String(limit));
      params.append('offset', String(offset));

      const response = await apiClient.get<BackendAuditLogsResponse>(`/api/v1/audit?${params.toString()}`);

      // Transform response from snake_case to camelCase
      const transformedLogs = response.data.logs.map(transformAuditLog);

      return {
        logs: transformedLogs,
        total: response.data.total,
        limit: response.data.limit,
        offset: response.data.offset,
      };
    },
    staleTime: 1000 * 30, // 30 seconds
    enabled: !!teamId, // Backend requires team_id
  });

  return {
    logs: auditLogsQuery.data?.logs ?? [],
    total: auditLogsQuery.data?.total ?? 0,
    currentPage: page,
    pageSize: pageSize,
    totalPages: Math.ceil((auditLogsQuery.data?.total ?? 0) / pageSize),
    isLoading: auditLogsQuery.isLoading,
    isFetching: auditLogsQuery.isFetching,
    error: auditLogsQuery.error,
    refetch: auditLogsQuery.refetch,
  };
}

/**
 * Get a human-readable description of an audit action
 */
export function getActionDescription(action: string): string {
  const actionInfo = AUDIT_ACTIONS.find((a) => a.value === action);
  return actionInfo?.label ?? action;
}

/**
 * Get action category (for styling)
 */
export function getActionCategory(action: string): 'create' | 'read' | 'update' | 'delete' | 'other' {
  // Backend uses past tense: secret.created, secret.updated, etc.
  if (action.endsWith('.created') || action.endsWith('.create') || action.includes('_invited') || action.includes('.invite')) return 'create';
  if (action.endsWith('.read') || action.endsWith('.export')) return 'read';
  if (action.endsWith('.updated') || action.endsWith('.update')) return 'update';
  if (action.endsWith('.deleted') || action.endsWith('.delete') || action.endsWith('.revoked') || action.endsWith('.revoke') || action.includes('_removed') || action.includes('.remove')) return 'delete';
  return 'other';
}
